import { useState, useEffect } from 'react'
import type { FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useOffice } from '../contexts/OfficeContext'
import { useLang } from '../contexts/LangContext'
import { supabase } from '../lib/supabase'
import { ShieldLogo } from '../components/ShieldLogo'
import { LangThemePicker } from '../components/LangThemePicker'

// Map a Supabase auth error to a localized product message. Bad email/password
// gets a clear credential message; a network/server failure keeps its own distinct
// message (never collapsed into "wrong password"); anything else is a safe generic.
// The raw provider text (e.g. "Invalid login credentials") is never shown.
function loginErrorMessage(error: { code?: string; status?: number; name?: string; message?: string }, t: (k: string) => string): string {
  const code = (error?.code ?? '').toString()
  const status = Number(error?.status ?? 0)
  const msg = (error?.message ?? '').toString().toLowerCase()
  if (error?.name === 'AuthRetryableFetchError' || status === 0 || status >= 500 || /failed to fetch|networkerror|network request|fetch/.test(msg)) {
    return t('auth.error.network')
  }
  if (code === 'invalid_credentials' || msg.includes('invalid login credentials')) {
    return t('auth.error.invalidCredentials')
  }
  return t('auth.error.generic')
}

// Animated shield with expanding ripple rings
function ShieldRipple() {
  return (
    <div className="relative flex items-center justify-center" style={{ width: 220, height: 220 }}>
      {/* Ripple rings — staggered */}
      {[0, 0.8, 1.6, 2.4].map((delay, i) => (
        <span
          key={i}
          className="ripple-ring"
          style={{ animationDelay: `${delay}s`, opacity: 0 }}
        />
      ))}
      {/* Shield — fills the circle */}
      <ShieldLogo size={180} />
    </div>
  )
}

export default function Login() {
  const { signIn, user } = useAuth()
  const { loading: officeLoading, onboardingCompleted, officeId } = useOffice()
  const { t } = useLang()
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [rememberMe, setRememberMe] = useState(true)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  const ADMIN_EMAIL = 'immigrantshield@gmail.com'

  useEffect(() => {
    if (!user) return
    if (user.email?.toLowerCase() === ADMIN_EMAIL.toLowerCase()) {
      supabase.auth.signOut()
      return
    }
    if (officeLoading || onboardingCompleted === null) return
    if (onboardingCompleted === false && !officeId) {
      navigate('/onboarding', { replace: true })
      return
    }
    navigate('/overview', { replace: true })
  }, [user, officeLoading, onboardingCompleted, officeId, navigate])

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError('')
    if (email.toLowerCase() === ADMIN_EMAIL) {
      setError(t('auth.adminAccountBlocked'))
      return
    }
    setLoading(true)
    const { error } = await signIn(email, password)
    if (error) {
      setError(loginErrorMessage(error, t))
      setLoading(false)
    } else {
      if (!rememberMe) {
        localStorage.setItem('session_only', '1')
      } else {
        localStorage.removeItem('session_only')
      }
      sessionStorage.setItem('session_active', '1')
      navigate('/overview')
    }
  }

  const bullets = [t('landing.trust1'), t('landing.trust3'), t('landing.trust4')]

  return (
    <div className="min-h-screen lg:h-screen lg:overflow-hidden flex flex-col lg:flex-row" style={{ backgroundColor: '#070E0A' }}>

      {/* ── Lang picker — fixed top-right (all viewports) ── */}
      <div className="fixed top-3 right-4 z-50">
        <LangThemePicker hideTheme />
      </div>

      {/* ── Left brand panel ── */}
      <div
        className="lg:w-[46%] flex-shrink-0 flex flex-col relative overflow-hidden"
        style={{ background: 'linear-gradient(160deg, #071410 0%, #0D2018 60%, #0A1A12 100%)' }}
      >
        {/* Decorative glows */}
        <div className="pointer-events-none absolute top-0 right-0 w-[500px] h-[500px] rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(29,158,117,0.1) 0%, transparent 70%)', transform: 'translate(20%, -30%)' }} />
        <div className="pointer-events-none absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(29,158,117,0.07) 0%, transparent 70%)', transform: 'translate(-20%, 20%)' }} />

        {/* Mobile: compact brand strip */}
        <div className="lg:hidden px-6 pt-6 pb-8 flex flex-col items-center text-center relative z-10">
          <Link to="/" className="flex items-center gap-2.5 mb-5 hover:opacity-80 transition-opacity">
            <ShieldLogo size={28} />
            <div className="text-left">
              <div className="font-bold text-white text-sm leading-tight">Immigrant Shield</div>
              <div className="text-xs font-medium leading-tight" style={{ color: '#1D9E75' }}>for Offices</div>
            </div>
          </Link>
          <ShieldRipple />
          <h2 className="text-xl font-bold text-white mt-4 leading-snug">
            {t('landing.headline1')}<br />
            <span style={{ color: '#1D9E75' }}>{t('landing.headline2')}</span>
          </h2>
        </div>

        {/* Desktop: full left content */}
        <div className="hidden lg:flex flex-col flex-1 justify-center px-10 pb-10 relative z-10">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 mb-8 hover:opacity-80 transition-opacity">
            <ShieldLogo size={32} />
            <div>
              <div className="font-bold text-white text-sm leading-tight">Immigrant Shield</div>
              <div className="text-xs font-medium leading-tight" style={{ color: '#1D9E75' }}>for Offices</div>
            </div>
          </Link>

          {/* Animated shield */}
          <div className="flex justify-center mb-6">
            <ShieldRipple />
          </div>

          {/* Headline */}
          <h2 className="text-2xl font-bold text-white leading-snug mb-5">
            {t('landing.headline1')}<br />
            <span style={{ color: '#1D9E75' }}>{t('landing.headline2')}</span>
          </h2>

          {/* Bullets */}
          <div className="space-y-3 mb-6">
            {bullets.map((item) => (
              <div key={item} className="flex items-center gap-3">
                <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: 'rgba(29,158,117,0.2)' }}>
                  <svg className="w-3 h-3" style={{ color: '#1D9E75' }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                  </svg>
                </div>
                <span className="text-sm" style={{ color: '#8EA89E' }}>{item}</span>
              </div>
            ))}
          </div>

        </div>

        {/* Bottom live indicator — desktop only */}
        <div className="hidden lg:flex relative z-10 items-center gap-2 px-10 pb-7">
          <div className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: '#1D9E75' }} />
          <span className="text-xs" style={{ color: '#4D7066' }}>{t('landing.trust1')} · {t('landing.trust2')}</span>
        </div>
      </div>

      {/* ── Right form panel ── */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-10 lg:py-0 overflow-y-auto"
        style={{ backgroundColor: '#0A0F0D' }}>

        <div className="w-full max-w-[400px]">

          {/* Header */}
          <div className="mb-8">
            <h1 className="text-2xl font-bold mb-2 text-white">
              {t('auth.signIn')}
            </h1>
            <p className="text-sm" style={{ color: '#6B8A7E' }}>
              {t('auth.noAccount')}{' '}
              <Link to="/onboarding" className="font-semibold hover:underline transition-opacity hover:opacity-80" style={{ color: '#1D9E75' }}>
                {t('auth.registerOffice')}
              </Link>
            </p>
          </div>

          {/* Error */}
          {error && (
            <div className="rounded-xl px-4 py-3 text-sm mb-6 flex items-start gap-2.5"
              style={{ backgroundColor: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.18)', color: '#f87171' }}>
              <svg className="w-4 h-4 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
              </svg>
              <span>{error}</span>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium mb-1.5 text-white">
                {t('auth.email')}
              </label>
              <input
                id="email" type="email" required
                value={email} onChange={e => setEmail(e.target.value)}
                placeholder={t('auth.emailPlaceholder')}
                className="w-full rounded-xl px-4 py-3 text-sm transition-all outline-none"
                style={{
                  backgroundColor: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  color: '#E5E7EB',
                }}
                onFocus={e => (e.target.style.borderColor = 'rgba(29,158,117,0.5)')}
                onBlur={e => (e.target.style.borderColor = 'rgba(255,255,255,0.1)')}
              />
            </div>

            {/* Password with show/hide */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium mb-1.5 text-white">
                {t('auth.password')}
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={password} onChange={e => setPassword(e.target.value)}
                  placeholder={t('auth.passwordPlaceholder')}
                  className="w-full rounded-xl px-4 py-3 pr-11 text-sm transition-all outline-none"
                  style={{
                    backgroundColor: 'rgba(255,255,255,0.04)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    color: '#E5E7EB',
                  }}
                  onFocus={e => (e.target.style.borderColor = 'rgba(29,158,117,0.5)')}
                  onBlur={e => (e.target.style.borderColor = 'rgba(255,255,255,0.1)')}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded transition-opacity hover:opacity-70"
                  style={{ color: '#6B8A7E' }}
                  tabIndex={-1}
                  aria-label={showPassword ? t('auth.hidePassword') : t('auth.showPassword')}
                >
                  {showPassword ? (
                    <svg className="w-4.5 h-4.5 w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                    </svg>
                  ) : (
                    <svg className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {/* Remember me + Forgot password */}
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2.5 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={e => setRememberMe(e.target.checked)}
                  className="w-4 h-4 rounded cursor-pointer accent-[#1D9E75]"
                />
                <span className="text-sm" style={{ color: '#6B8A7E' }}>{t('auth.rememberMe')}</span>
              </label>
              <Link to="/forgot-password"
                className="text-xs font-medium hover:underline transition-opacity hover:opacity-80"
                style={{ color: '#1D9E75' }}>
                {t('auth.forgotPassword')}
              </Link>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-xl py-3 text-sm font-semibold text-white transition-all hover:opacity-90 active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              style={{
                background: 'linear-gradient(135deg, #1D9E75, #14b87f)',
                boxShadow: '0 4px 20px rgba(29,158,117,0.35)',
              }}
            >
              {loading ? (
                <>
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  {t('auth.signingIn')}
                </>
              ) : (
                <>{t('auth.submit')} &rarr;</>
              )}
            </button>
          </form>

          {/* Trust badges */}
          <div className="mt-8 pt-6 flex items-center justify-center gap-5 flex-wrap"
            style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
            {[
              { icon: '🔒', label: t('auth.trust.secure') ?? 'Conexão segura' },
              { icon: '🛡️', label: t('auth.trust.protected') ?? 'Dados protegidos' },
              { icon: '✓', label: t('auth.trust.ssl') ?? 'SSL verificado' },
            ].map(({ icon, label }) => (
              <div key={label} className="flex items-center gap-1.5">
                <span className="text-xs" style={{ color: '#1D9E75' }}>{icon}</span>
                <span className="text-xs" style={{ color: '#3D5F52' }}>{label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
